import { useQueries } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { type DashboardCategoryConfig } from 'map-3d/components';
import { useContext, useMemo } from 'react';
import {
  DashboardCategoryEnum,
  type DashboardDataCategoryEntry,
  getDashboardCategoryEntries,
  useDashboardCategoryLabels,
} from '../api';
import { GlobalContext } from '../context';
import { FeatureFlag } from '../enums';

export const useDashboardCategoryConfig = (enabled = false) => {
  // Context.
  const { loggedUser } = useContext(GlobalContext);

  // Constants.
  const dashboardCategoryKeys = Object.values(DashboardCategoryEnum);
  const isProductivityDashboardEnabled = Boolean(
    loggedUser?.featureFlags?.[FeatureFlag.ProductivityDashboard],
  );

  // Apis.
  const {
    data: dashboardCategoryLabels,
    isLoading: isDashboardCategoryLabelsLoading,
    isError: isDashboardCategoryLabelsError,
    refetch: dashboardCategoryLabelsRefetch,
  } = useDashboardCategoryLabels(isProductivityDashboardEnabled);

  const categoryQueries = useQueries({
    queries: dashboardCategoryKeys.map((category: DashboardCategoryEnum) => ({
      queryKey: ['dashboard-category-entries', category],
      queryFn: () =>
        getDashboardCategoryEntries({
          category,
        }),
      enabled: isProductivityDashboardEnabled && enabled,
      refetchOnWindowFocus: false,
    })),
  });

  // Variables.
  const isLoading =
    isProductivityDashboardEnabled &&
    (isDashboardCategoryLabelsLoading ||
      categoryQueries.some(({ isLoading }) => isLoading));

  const isError =
    isDashboardCategoryLabelsError ||
    categoryQueries.some(({ isError }) => isError);

  // Memo.
  const dashboardCategoryConfig: DashboardCategoryConfig[] = useMemo(() => {
    if (!isProductivityDashboardEnabled) {
      return [];
    }

    const categoryLabels = dashboardCategoryLabels?.data?.labels;

    if (isNil(categoryLabels)) {
      return [];
    }

    return dashboardCategoryKeys.map(
      (category: DashboardCategoryEnum, index) => ({
        categoryKey: category,
        name: categoryLabels[category],
        options:
          categoryQueries[index]?.data?.data?.entries?.map(
            (entry: DashboardDataCategoryEntry) => ({
              label: entry.name,
              value: entry,
            }),
          ) ?? [],
      }),
    );
  }, [
    isProductivityDashboardEnabled,
    dashboardCategoryLabels?.data,
    categoryQueries,
  ]);

  const refetchDashboardConfig = async () => {
    if (!isProductivityDashboardEnabled) {
      return;
    }

    await Promise.all([
      dashboardCategoryLabelsRefetch(),
      ...categoryQueries.map(({ refetch }) => refetch()),
    ]);
  };

  return {
    dashboardCategoryLabels: dashboardCategoryLabels?.data?.labels ?? null,
    dashboardCategoryConfig,
    isLoading,
    isError,
    refetchDashboardConfig,
  };
};
